import { splitFullName } from './rosterInlineEdit'
import type { EditableOfficialField } from './officialInlineEdit'

export interface OfficialUpdateSource {
  name: string
  officialTypeId: string
  email: string
  phone: string
  phoneCountry?: string
}

export interface OfficialUpdatePayload {
  name_first: string
  name_last: string
  team_official_type_id: number
  email?: string
  phone_number?: string
  phone_number_country?: string
}

/** Apply one inline cell edit to an official's field set. */
export const applyOfficialInlineEdit = (
  source: OfficialUpdateSource,
  field: EditableOfficialField,
  rawValue: string
): OfficialUpdateSource => {
  const value = (rawValue ?? '').trim()

  switch (field) {
    case 'name':
      return { ...source, name: value }
    case 'role':
      return { ...source, officialTypeId: value }
    case 'email':
      return { ...source, email: value }
    case 'phone':
      return { ...source, phone: value }
    default:
      return source
  }
}

/**
 * Build the body for `PUT /teams/:t/rosters/:r/team-officials/:id`.
 *
 * Unlike the player endpoint — which takes a DIFF (see `toRosterUpdatePayload`)
 * — this one is a FULL REPLACE, so every field the official currently has must
 * be resent or it would be blanked by a single-cell edit. Mirrors the payload
 * `TeamOfficialContext.onSubmit` sends, including omitting empty optional
 * fields rather than sending empty strings.
 */
export const toOfficialUpdatePayload = (
  source: OfficialUpdateSource
): OfficialUpdatePayload => {
  const { firstName, lastName } = splitFullName(source.name)

  const payload: OfficialUpdatePayload = {
    name_first: firstName,
    name_last: lastName,
    team_official_type_id: Number(source.officialTypeId),
  }

  const email = source.email.trim()
  if (email) {
    payload.email = email
  }

  const phone = source.phone.trim()
  if (phone) {
    payload.phone_number = phone
    payload.phone_number_country = source.phoneCountry || 'US'
  }

  return payload
}
